import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export const handleAuthError = (queryClient = null) => {
  // Ignore 401s that arrive within 10 seconds of a fresh login —
  // these are stale in-flight requests that raced with the new token.
  const storedAt = parseInt(localStorage.getItem('authTokenStoredAt') || '0', 10);
  if (Date.now() - storedAt < 10000) return;

  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('userId');
  localStorage.removeItem('username');
  localStorage.removeItem('is_superuser');
  localStorage.removeItem('authTokenStoredAt');
  if (queryClient) {
    queryClient.clear();
  }
  window.dispatchEvent(new CustomEvent('tokenExpired'));
};

// Evaluated at hook-call time (inside the component render cycle),
// not at module-load time, so it correctly reflects the current
// localStorage state after login has stored the token.
const hasToken = () => !!localStorage.getItem('authToken');

// Hook to fetch client's own quotations
// NOTE: If backend doesn't filter by user, we'll need to add query params or filter on frontend
export const useClientQuotations = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientQuotations'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using the main quotations endpoint
      // If backend filters by logged-in user automatically, this works
      // Otherwise, add ?user=me or filter results client-side
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/quotations/?format=json`, { headers });
      } catch (err) {
        if (err.response?.status === 401) { handleAuthError(); }
        throw err;
      }

      // Handle both paginated and non-paginated responses
      let quotations = response.data.results || response.data || [];
      
      // If backend doesn't filter by user, we might need to filter here
      // Uncomment if needed:
      // const currentUserId = getCurrentUserId(); // You'll need to implement this
      // quotations = quotations.filter(q => q.user === currentUserId || q.user_info?.id === currentUserId);
      
      return quotations;
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
    enabled: hasToken(),
  });
};

// Hook to fetch client's follow-ups
export const useClientFollowups = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientFollowups'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /api/followups/ endpoint
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/followups/?format=json`, { headers });
      } catch (err) {
        if (err.response?.status === 401) { handleAuthError(); }
        throw err;
      }
      let followups = response.data.results || response.data || [];
      
      // Filter by current user if needed
      // followups = followups.filter(f => f.quotation?.user === currentUserId);
      
      return followups;
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 1 minute)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
    enabled: hasToken(),
  });
};

// Hook to fetch client's documents
export const useClientDocuments = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientDocuments'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /api/documents/ endpoint
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/documents/?format=json`, { headers });
      } catch (err) {
        if (err.response?.status === 401) { handleAuthError(); }
        throw err;
      }
      let documents = response.data.results || response.data || [];
      
      // Filter by current user if needed
      // documents = documents.filter(d => d.quotation?.user === currentUserId);
      
      return documents;
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 5 minutes)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
    enabled: hasToken(),
  });
};

// Hook to fetch client's notifications
export const useClientNotifications = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientNotifications'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /api/notifications/ endpoint
      // This should already be filtered by user on the backend
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/api/notifications/?format=json`, { headers });
      } catch (err) {
        if (err.response?.status === 401) { handleAuthError(); }
        throw err;
      }
      return response.data.results || response.data || [];
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 30 seconds)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
    enabled: hasToken(),
  });
};

// Hook to fetch client's profile
export const useClientProfile = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientProfile'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /rest-auth/user-profile/ endpoint
      let response;
      try {
        response = await axios.get(`${API_BASE_URL}/rest-auth/user-profile/`, { headers });
      } catch (err) {
        if (err.response?.status === 401) { handleAuthError(); }
        throw err;
      }
      return response.data || {};
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 5 minutes)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
    enabled: hasToken(),
  });
};

// Hook to fetch user profile details (alternative endpoint)
export const useClientProfileDetails = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientProfileDetails'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /rest-auth/user-profile/details/ endpoint
      const response = await axios.get(`${API_BASE_URL}/rest-auth/user-profile/details/`, { headers });
      return response.data || {};
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 5 minutes)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
  });
};

// Hook to fetch client dashboard stats
export const useClientDashboardStats = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientDashboardStats'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /dashboard/stats/ endpoint
      // This should ideally be modified on backend to return user-specific stats for clients
      // For now, we'll use it and potentially filter or calculate client-specific stats
      const response = await axios.get(`${API_BASE_URL}/dashboard/stats/`, { headers });
      return response.data || {
        total_quotations: 0,
        pending_quotations: 0,
        completed_quotations: 0,
        total_estimated_value: 0,
        recent_quotations: 0,
        today_quotations: 0,
        total_customers: 0,
      };
    },
    staleTime: 3 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 0,   // Don't retry - this endpoint requires admin access
    enabled: false, // Disabled for client dashboard - clients use calculatedStats instead
  });
};

export const useClientDetailedStats = (API_BASE_URL) => {
  return useQuery({
    queryKey: ['clientDetailedStats'],
    queryFn: async () => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Using /dashboard/detailed-stats/ endpoint
      const response = await axios.get(`${API_BASE_URL}/dashboard/detailed-stats/`, { headers });
      return response.data || {};
    },
    staleTime: 3 * 60 * 1000, // 2 minutes (previously 5 minutes)
    gcTime: 10 * 60 * 1000,   // Keep cache for 10 minutes after unmount
    retry: 1,
  });
};

/**
 * Helper function to calculate client-specific stats from quotations
 * Use this if the backend stats endpoint returns system-wide stats instead of user-specific
 */
// ClientDashboardQueries.js
// Update the calculateClientStats function:

export const calculateClientStats = (quotations) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  return {
    total_quotations: quotations.length,
    pending_quotations: quotations.filter(q => 
      q.status === 'submitted' || q.status === 'contacted'
    ).length,
    completed_quotations: quotations.filter(q => 
      q.status === 'converted' || q.status === 'completed'
    ).length,
    cancelled_quotations: quotations.filter(q => 
      q.status === 'cancelled' || q.status === 'expired'
    ).length,
    
    // FIX: Use total_estimated_cost instead of estimated_value
    total_estimated_value: quotations.reduce((sum, q) => 
      sum + (parseFloat(q.total_estimated_cost) || 0), 0
    ),
    recent_quotations: quotations.filter(q => {
      const createdDate = new Date(q.created_at);
      const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
      return diffDays <= 7; // Last 7 days
    }).length,
    today_quotations: quotations.filter(q => {
      const createdDate = new Date(q.created_at);
      return createdDate >= today;
    }).length,
  };
};

// Master hook that fetches all client dashboard data
export const useAllClientDashboardData = (API_BASE_URL, queryClient) => {
  const statsQuery = useClientDashboardStats(API_BASE_URL);
  const quotationsQuery = useClientQuotations(API_BASE_URL);
  const followupsQuery = useClientFollowups(API_BASE_URL);
  const documentsQuery = useClientDocuments(API_BASE_URL);
  const notificationsQuery = useClientNotifications(API_BASE_URL);
  const profileQuery = useClientProfile(API_BASE_URL);

  // Calculate client-specific stats from quotations
  const quotations = quotationsQuery.data || [];
  const clientCalculatedStats = calculateClientStats(quotations);

  // Derive solar/electrical data from quotations - no separate endpoints exist
  // service_type is a field on Quotation: 'solar', 'electrical', or 'both'
  const solarQuotations = quotations.filter(q => q.service_type === 'solar' || q.service_type === 'both');
  const electricalQuotations = quotations.filter(q => q.service_type === 'electrical' || q.service_type === 'both');
  const electricalServices = electricalQuotations;

  return {
    // Data
    stats: statsQuery.data || clientCalculatedStats,
    calculatedStats: clientCalculatedStats,
    quotations: quotations,
    solarQuotations: solarQuotations,
    electricalQuotations: electricalQuotations,
    electricalServices: electricalServices,
    followups: followupsQuery.data || [],
    documents: documentsQuery.data || [],
    notifications: notificationsQuery.data || [],
    profile: profileQuery.data || {},
    
    // isLoading = true ONLY on initial fetch with no cached data
    // Navigating back while cache is valid will NOT trigger isLoading
    isLoading:
      quotationsQuery.isPending ||
      followupsQuery.isPending ||
      documentsQuery.isPending ||
      notificationsQuery.isPending ||
      profileQuery.isPending,

    // isFetching = true during silent background refetches (data visible, refreshing behind the scenes)
    isFetching:
      quotationsQuery.isFetching ||
      followupsQuery.isFetching ||
      documentsQuery.isFetching ||
      notificationsQuery.isFetching ||
      profileQuery.isFetching,

    // Error states
    isError:
      quotationsQuery.isError ||
      followupsQuery.isError ||
      documentsQuery.isError ||
      notificationsQuery.isError ||
      profileQuery.isError,

    // Individual loading states
    loadingStates: {
      quotations: quotationsQuery.isLoading,
      followups: followupsQuery.isLoading,
      documents: documentsQuery.isLoading,
      notifications: notificationsQuery.isLoading,
      profile: profileQuery.isLoading,
    },

    // Error details
    errors: {
      quotations: quotationsQuery.error,
      followups: followupsQuery.error,
      documents: documentsQuery.error,
      notifications: notificationsQuery.error,
      profile: profileQuery.error,
    },

    // Refetch all real queries (solar/electrical are derived, not fetched separately)
    refetchAll: () => {
      quotationsQuery.refetch();
      followupsQuery.refetch();
      documentsQuery.refetch();
      notificationsQuery.refetch();
      profileQuery.refetch();
      // Also invalidate the Topbar's user profile query so the displayed
      // name/avatar updates immediately without requiring a logout/login
      if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      }
    },

    // Individual refetch functions
    refetch: {
      quotations: quotationsQuery.refetch,
      followups: followupsQuery.refetch,
      documents: documentsQuery.refetch,
      notifications: notificationsQuery.refetch,
      profile: profileQuery.refetch,
    }
  };
};